/* eslint-disable no-restricted-globals */
import type { MappingSchemaJSON, MappingSchemaRow } from "interfaces/dataInterfaces"
import type { MappingPriceOption } from "interfaces/mappingInterfaces"

export const mappingOptions: MappingPriceOption[] = [
  {
    label: "Geographical data",
    options: [{ label: "Market Name or ID", value: "market", type: "market" }],
  },
  {
    label: "Commodity",
    options: [{ label: "Commodity Name", value: "commodity", type: "commodity" }],
  },
  {
    label: "Price",
    options: [
      { label: "Price Value", value: "priceValue", type: "price" },
      { label: "Price of a Specific Commodity", value: "priceCommodity", type: "price", commodity: true },
      {
        label: "Price of a Specific Commodity with Unit of Measure",
        value: "priceCommodityMeasure",
        type: "price",
        commodity: true,
        unit: true,
      },
      { label: "Price Type", value: "priceType", type: "priceType" },
    ],
  },
  {
    label: "Unit of Measure",
    options: [
      { label: "Unit Name", value: "unitName", type: "unit" },
      {
        label: "Unit of Measure of a Specific Commodity",
        value: "unitCommodity",
        type: "unit",
        commodity: true,
        unit: true,
      },
    ],
  },
  {
    label: "Currency",
    options: [{ label: "Currency Name", value: "currency", type: "currency" }],
  },
  {
    label: "Date",
    options: [
      { label: "Day", value: "day", type: "date" },
      { label: "Month", value: "month", type: "date" },
      { label: "Year", value: "year", type: "date" },
      { label: "Full Date (dd/mm/yyyy)", value: "dd/MM/yyyy", type: "date" },
      { label: "Full Date (yyyy/mm/dd)", value: "yyyy/MM/dd", type: "date" },
      { label: "Full Date (mm/dd/yyyy)", value: "MM/dd/yyyy", type: "date" },
    ],
  },
  {
    label: "Other",
    options: [
      { label: "Data source", value: "dataSource", type: "dataSource" },
      { label: "Metadata", value: "metadata", type: "metadata" },
    ],
  },
]

const checkIsDataTypeMapped = (value: string, list: MappingSchemaRow[]): boolean => {
  return !!list.find(item => item.dataType.toLowerCase() === value.toLowerCase())
}

const checkIsTypeMapped = (value: string, list: MappingSchemaRow[]): boolean =>
  !!list.find(item => item.type.toLowerCase() === value.toLowerCase())

export const mappingRules = {
  market: {
    validation: (value: string, list: MappingSchemaRow[]) => checkIsDataTypeMapped(value, list),
  },
  commodity: {
    validation: (value: string, list: MappingSchemaRow[]) => {
      const values = ["priceCommodity", "priceCommodityMeasure", "unitCommodity"]
      return !!list.find(item => item.dataType.toLowerCase() === value.toLowerCase() || values.includes(item.dataType))
    },
  },
  currency: {
    validation: (value: string, list: MappingSchemaRow[], schema: MappingSchemaJSON) =>
      checkIsDataTypeMapped(value, list) || schema.currency,
  },

  day: {
    validation: (value: string, list: MappingSchemaRow[]) => {
      const values = ["dd/MM/yyyy", "yyyy/MM/dd", "MM/dd/yyyy"]
      return !!list.find(item => item.dataType.toLowerCase() === value.toLowerCase() || values.includes(item.dataType))
    },
  },
  month: {
    validation: (value: string, list: MappingSchemaRow[]) => {
      const values = ["dd/MM/yyyy", "yyyy/MM/dd", "MM/dd/yyyy"]
      return !!list.find(item => item.dataType.toLowerCase() === value.toLowerCase() || values.includes(item.dataType))
    },
  },
  year: {
    validation: (value: string, list: MappingSchemaRow[]) => {
      const values = ["dd/MM/yyyy", "yyyy/MM/dd", "MM/dd/yyyy"]

      return !!list.find(item => item.dataType.toLowerCase() === value.toLowerCase() || values.includes(item.dataType))
    },
  },
  "dd/MM/yyyy": { validation: (_: string, list: MappingSchemaRow[]) => checkIsTypeMapped("date", list) },
  "yyyy/MM/dd": { validation: (_: string, list: MappingSchemaRow[]) => checkIsTypeMapped("date", list) },
  "MM/dd/yyyy": {
    validation: (_: string, list: MappingSchemaRow[]) => checkIsTypeMapped("date", list),
  },

  priceValue: {
    validation: (value: string, list: MappingSchemaRow[]) => {
      const values = ["priceCommodity", "priceCommodityMeasure"]
      return !!list.find(item => item.dataType.toLowerCase() === value.toLowerCase() || values.includes(item.dataType))
    },
  },
  priceCommodity: {
    validation: (_: string, list: MappingSchemaRow[]) => {
      const values = ["priceValue", "priceCommodityMeasure", "commodity", "unitName"]
      return !!list.find(item => values.includes(item.dataType))
    },
  },
  priceCommodityMeasure: {
    validation: (_: string, list: MappingSchemaRow[]) => {
      const values = ["priceValue", "priceCommodity", "commodity", "unitName", "unitCommodity"]
      return !!list.find(item => values.includes(item.dataType))
    },
  },
  priceType: {
    validation: (value: string, list: MappingSchemaRow[], schema: MappingSchemaJSON) =>
      checkIsDataTypeMapped(value, list) || schema.priceType,
  },

  unitName: {
    validation: (value: string, list: MappingSchemaRow[]) => {
      const values = ["priceCommodityMeasure", "unitCommodity", "priceCommodity"]
      return !!list.find(item => item.dataType.toLowerCase() === value.toLowerCase() || values.includes(item.dataType))
    },
  },
  unitCommodity: {
    validation: (_: string, list: MappingSchemaRow[]) => {
      const values = ["unitName", "priceCommodityMeasure", "commodity"]
      return !!list.find(item => values.includes(item.dataType))
    },
  },

  metadata: { validation: () => false },
  dataSource: { validation: (value: string, list: MappingSchemaRow[]) => checkIsDataTypeMapped(value, list) },
}

export const requiredFields = [
  { label: "Frequency", field: "frequency" },
  { label: "Date", field: "date" },
  { label: "Market", field: "market" },
  { label: "Commodity", field: "commodity", commodity: true },
  { label: "Unit", field: "unit", unit: true },
  { label: "Price", field: "price" },
  { label: "Price type", field: "priceType" },
  { label: "Data source", field: "dataSource" },
  { label: "Currency", field: "currency" },
]

export enum ReportStatuses {
  InProgress = 1,
  Pending = 3,
  Approved = 4,
  Rejected = 5,
  ApprovedInternal = 9,
}

export enum CommodityPriceStatus {
  Approved = 3,
  Rejected = 4,
  ApprovedInternal = 5,
}

export enum MappingSchemaStatus {
  Complete = 4,
}
